/**
 * @file src/shame/shame-atoms.ts
 * @exports Multiple atoms and helpers for Cognition and vID Web state.
 * @summary Application-wide atoms and vID IndexedDB utilities; invite form state stays local to its page.
 */

import { atom } from "jotai";
import type {
	BeamListQuery,
	DisclosureAddress,
	IdCaptureMethod,
	Order,
	Organization,
	OrganizationsQuery,
	SortOrder,
	VidVoucherQuery,
} from "../generated/graphql";
import { atomWithReset, atomWithStorage, RESET } from "jotai/utils";
import type { ActiveFiltersType, FilterType, RowType, StepType } from "@cerebruminc/cerebellum";
import type { FiltersObjectType, InviteError } from "src/views/types";
import { type ExtendedFilterType, type SearchTypesEnumValues } from "src/views/OrdersView/types";
import { GenericFilterModalFiltersType } from "src/components/FiltersModal/types";
import { ExtendedInviteFilterType, InviteSearchTypes, InvitesFilterModalFilters } from "src/views/Invites/types";
import { SubOrganizationType } from "src/views/SubOrgView/types";
import { defaultVidSteps, IdCategory, InternationalIdType, UsIdType, VidStepsEnum } from "src/views/VID/const";
import { StringBooleanType } from "src/types";
import { OrgContactInfo } from "src/pages/vid/i/[invite]";
import { AdditionalPersonalInformation, ScanResult } from "src/views/VID/views/types";
import { ScanError } from "src/views/VID/types";

// ==============================
// Global atoms
// ==============================

interface WindowSizeType {
	height: number;
	width: number;
}

export const windowSizeAtom = atom<WindowSizeType>({ height: 0, width: 0 });

export const currentOrgIdAtom = atom<string | null>(null);
export const forcedTableLoadingAtom = atom<boolean>(false);
export const routerQueryStateAtom = atom({});

export const bannerShownAtom = atom(false);
export const authCheckAtom = atom(false);

/** True while Cognition cannot reach its backend (GraphQL outage / network failure). */
export const serviceUnavailableAtom = atom(false);

// This disables the "alwaysScroll" class on the body when a modal is open. It's not necessary for all modals.
export const modalOpenAtom = atom(false);

// This is a simple counter used to trigger data fetching when a new org is created
export const orgCreationAtom = atom(0);

// Tracks whether vID session cleanup has been performed in this runtime
export const didRunVidSessionCleanupAtom = atom<boolean>(false);

// Atom to store resend code time for 1 minute
export const resendCodeTimeAtom = atomWithStorage<number>("resendCodeTime", 60);

// ==============================
// FiltersModal atoms
// ==============================

// These atoms needs to be wrapped in a provider with a unique store for each
// instance of the filters modal. This is because atoms are global and we don't
// want the filters modal to share state between instances. Failing to wrap with
// a provider will cause the filters modal to share state between instances and
// will cause unexpected behavior.

// The active filter state - this is displayed in FiltersBar and in the Table. It's what actually filters the data
export const activeFiltersAtom = atom<FilterType[]>([]);
export const activeTableFiltersAtom = atom<ActiveFiltersType>({}); // requires a very different format than the rest of the components

// filterModalFilters is an object with the data for the RadioGroups or CheckboxGroups in the FilterModal
// - it gets updated whenever activeFilters does
// - it also gets updated when the FilterModal radios or checkboxes are changed
// - it gets reset to the activeFilter state when FilterModal closes (whether the filters are applied or not)
export const filterModalFiltersAtom = atom<GenericFilterModalFiltersType>({});
export const filterModalCustomFromAtom = atom<number | undefined>(undefined);
export const tempFilterModalCustomFromAtom = atom<number | undefined>(undefined);
export const filterModalCustomToAtom = atom<number | undefined>(undefined);
export const tempFilterModalCustomToAtom = atom<number | undefined>(undefined);

export const draftFromDateAtom = atom<number | undefined>(undefined);
export const draftToDateAtom = atom<number | undefined>(undefined);

export const availableFiltersAtom = atom<FiltersObjectType>({});
export const currentSearchTypeAtom = atom<SearchTypesEnumValues | undefined>(undefined);
export const customDateFilterFromAtom = atom<number | undefined>(undefined);
export const customDateFilterToAtom = atom<number | undefined>(undefined);
export const defaultToDateTabAtom = atom<boolean>(false);
export const filtersModalOpenAtom = atom<boolean>(false);

export const orderTableCurrentPageAtom = atom<number>(0);

export const editSearchModeAtom = atom<boolean>(false);
export const searchInputValueAtom = atom<string>("");
export const searchTagsAtom = atom<ExtendedFilterType[]>([]);

export const showClearAllToastAtom = atom<boolean>(false);
export const stashedFiltersAtom = atom<ActiveFiltersType>({});
export const stashedSearchTagsAtom = atom<ExtendedFilterType[]>([]);

// Note that this has the same name as the global windowSizeAtom, but is used for the filters modal
// TODO: deduplicate this
export const filtersWindowSizeAtom = atom<WindowSizeType>({
	height: 0,
	width: 0,
});

export const draftFiltersChangedAtom = atom((get) => {
	const draftState = get(filterModalFiltersAtom);
	const tableState = get(activeTableFiltersAtom);
	const draftFromDate = get(draftFromDateAtom);
	const draftToDate = get(draftToDateAtom);
	const tableFromDate = get(filterModalCustomFromAtom);
	const tableToDate = get(filterModalCustomToAtom);
	return {
		draftState,
		tableState,
		draftFromDate,
		draftToDate,
		tableFromDate,
		tableToDate,
	};
});

// ==============================
// MfaModal atoms
// ==============================
export const mfaStepAtom = atom(1);

// ==============================
// SupportButton atoms
// ==============================
export enum SupportModalState {
	FORM = "form",
	SUCCESS = "success",
	ERROR = "error",
}
export const openSupportModalAtom = atom<boolean>(false);
export const currentModalStateAtom = atom<SupportModalState>(SupportModalState.FORM);
export const supportModalAdditionalMessagesAtom = atom<string[]>([]);

// ==============================
// Accessibility atoms
// ==============================
export const highContrastThemeAtom = atomWithStorage<boolean>("accessibilityHighContrastTheme", false);
export const requireNotificationInteractionAtom = atomWithStorage<boolean>(
	"accessibilityRequireNotificationInteraction",
	false,
);

// ==============================
// ReturnTo atoms
// ==============================
export const returnToAtom = atom("");

// ==============================
// useGetAllOrganizations atoms
// ==============================

// We have to use a Jotai atom to keep the state consistent when this hook is used in multiple components.
export const organizationsAtom = atomWithReset<OrganizationsQuery["organizations"] | undefined>(undefined);

// ==============================
// useRouteHistory atoms
// ==============================
export const previousPathAtom = atom<string | null>(null);

// ==============================
// shared views atoms
// ==============================

// Invites
export const inviteErrorsAtom = atom<InviteError[]>([]);
export const loadingIdsAtom = atom<string[] | null>(null);
export const updatedRowIdsAtom = atom<string[] | null>(null);
export const shouldRefetchInvitesAtom = atom<boolean>(false);

// ==============================
// Invites atoms
// ==============================

// This will be used to track the URL state in case we change pages. This includes sorting, TODO: and in the future, filtering.
export const invitesTableURLStateAtom = atom({
	sort: "updatedAt",
	// `SortOrder.Desc` — use literal so this file stays `import type`-only from graphql (breaks fetcher↔atoms cycle).
	sortDirection: "desc" as SortOrder,
});

export const inviteTableCurrentPageAtom = atom<number>(0);

// The active filter state - this is displayed in FiltersBar and in the Table. It's what actually filters the data
export const activeInvitesFiltersAtom = atom<FilterType[]>([]);
export const activeInvitesTableFiltersAtom = atom<ActiveFiltersType>({}); // requires a very different format than the rest of the components

// Search
export const editInviteSearchModeAtom = atom<boolean>(false);
export const invitesDefaultToDateTabAtom = atom<boolean>(false);
export const invitesSearchInputValueAtom = atom<string>("");
export const invitesSearchTagsAtom = atom<ExtendedInviteFilterType[]>([]); // this actually filters the data
export const stashedInvitesSearchTagsAtom = atom<ExtendedInviteFilterType[]>([]);

// Filters
export const availableInvitesFiltersAtom = atom<FiltersObjectType>({});
export const currentInvitesSearchTypeAtom = atom<InviteSearchTypes | undefined>(undefined);
export const invitesFiltersModalOpenAtom = atom<boolean>(false);
// filterModalFilters is an object with the data for the RadioGroups or CheckboxGroups in the FilterModal
// - it gets updated whenever activeFilters does
// - it also gets updated when the FilterModal radios or checkboxes are changed
// - it gets reset to the activeFilter state when FilterModal closes (whether the filters are applied or not)
export const invitesFilterModalFiltersAtom = atom<InvitesFilterModalFilters>({});
export const showClearAllInvitesToastAtom = atom<boolean>(false);
export const stashedInvitesFiltersAtom = atom<ActiveFiltersType>({});
// Filters dates
export const customDateInvitesFilterFromAtom = atom<number | undefined>(undefined);
export const customDateInvitesFilterToAtom = atom<number | undefined>(undefined);
export const filterModalInvitesCustomFromAtom = atom<number | undefined>(undefined);
export const filterModalInvitesCustomToAtom = atom<number | undefined>(undefined);
export const tempFilterModalInvitesCustomFromAtom = atom<number | undefined>(undefined);
export const tempFilterModalInvitesCustomToAtom = atom<number | undefined>(undefined);

// ==============================
// SubOrgView atoms
// ==============================
export const subOrgPageLoadingAtom = atom<boolean>(false);
export const subOrganizationsListAtom = atom<SubOrganizationType[]>([]);

// ==============================
// UserView atoms
// ==============================

// ==============================
// Dispute Center atoms
// ==============================
// We need to store this to resend the verification code, but users won't want to see it in the URL
export const ssnLast4Atom = atom<string>("");

// User card data
interface UserCardData {
	createdAt?: string;
	dob?: string;
	email?: string;
	loading?: boolean;
	phone?: string;
	selfieUrl?: string;
	userName?: string;
	userVerified?: boolean;
}
export const userCardDataAtom = atom<UserCardData>({ loading: true });

// Passes data
export const passesAtom = atom<RowType[]>([]);
export const passesLoadingAtom = atom<boolean>(true);

// Credentials data
export const credentialsAtom = atom<RowType[]>([]);
export const credentialsLoadingAtom = atom<boolean>(true);

// ==============================
// VID atoms
// ==============================

const DEFAULT_STATE = "defaultState";
const defaultCompletedSteps = {
	[VidStepsEnum.Selfie]: false,
	[VidStepsEnum.IdUpload]: false,
	[VidStepsEnum.Ssn]: false,
	[VidStepsEnum.SignDisclosures]: false,
};

interface BeamDataFileIdsType {
	[key: string]: string[];
}

type BeamData = {
	// we use any here as beam data can be of any form
	[id: string]: any;
};

interface DataStore {
	[key: string]: VIDState;
}

// This holds the names for all of the vID state that is stored in local storage
// Each key should also have a matching entry in VIDState and defaultVIDStateValues below
export enum vIDStateName {
	// save the references for field files {[beamId]: [fileId1, fileId2, ...]}
	BeamDataFileIds = "beamDataFileIds",
	// save the references for the scanned files {[beamId]: [fileId1, fileId2, ...]}
	BeamDataScannedFileIds = "beamDataScannedFileIds",
	BeamInputFormData = "beamInputFormData",
	// Biometric consent for selfie collection
	BiometricConsent = "biometricConsent",
	// BiometricConsentAcceptance record created when the user signs biometric consent; attached to the order later
	BiometricConsentAcceptanceId = "biometricConsentAcceptanceId",
	// The IdCaptureMethod for the most recent scan. Used for analytics
	CaptureMethod = "captureMethod",
	CameraAvailable = "cameraAvailable",
	// Skip the SSN collection step or not
	CollectSSN = "collectSSN",
	CompletedVidSteps = "completedVidSteps",
	// The applicant's current address, which is used to determine Disclosures. Defaults to their scanned address, but can be changed in the personal-information view.
	CurrentAddress = "currentAddress",
	DisclosureAcceptanceId = "disclosureAcceptanceId",
	DisplayIdCaptureCamera = "displayIdCaptureCamera",
	DisplaySelfieCamera = "displaySelfieCamera",
	// Tracks when the user has voluntarily elected to enter their information manually (without meeting the number of failed attempts threshold)
	ElectedManualEntry = "electedManualEntry",
	ExtractedPhoto = "extractedPhoto",
	// Tracks if the uploaded image scanning failed. After
	// max failed attempts, the user will be prompted to enter the information manually
	// This applies to scans on the local device or remote capture device
	FailedUploadCount = "failedUploadCount",
	// Tracks if the user has already been shown the expired-ID warning dialog.
	// On a second scan attempt the user is allowed to proceed regardless of expiration.
	HasSeenExpiredIdWarning = "hasSeenExpiredIdWarning",
	// Is this is the remote capture device?
	// If true, the user is coming from the desktop with the selfie already taken
	IsIdentityRecheck = "isIdentityRecheck",
	IsRemoteCaptureDevice = "isRemoteCaptureDevice",
	// API key details
	KeyDetails = "keyDetails",
	// Most recent job id for the current scan. Only used in manual entry to pull the image IDs
	LatestScanJobId = "latestScanJobId",
	// Store the organization contact info for expired invites
	OrgContactInfo = "orgContactInfo",
	PassTypeBeamsData = "passTypeBeamsData",
	// Tracks if this device has received images from a remote capture device. Currently only used for analytics
	ReceivedRemoteCaptureImages = "receivedRemoteCaptureImages",
	// Turns on polling to listen for a scan on a different device. This gets activated if the user generates a QR code.
	RemoteCaptureQRGenerated = "remoteCaptureQRGenerated",
	RescanIdClick = "rescanIdClick",
	ReturningOneAndDoneUser = "returningOneAndDoneUser",
	// Delay before resending the unlock API key code
	ResendCodeTime = "resendCodeTime",
	// Skip the Liveness Detection step or not
	SkipLiveness = "skipLiveness",
	// Skip the Disclosure step or not
	ShowDisclosures = "showDisclosures",
	ShowManualEntryForAdditionalDetails = "showManualEntryForAdditionalDetails",
	ScanError = "scanError",
	// Embed vID Web in an iFrame (strips non-essential UI elements)
	EmbedVidWeb = "embedVidWeb",
	// The file ID of the uploaded selfie, once it's stored on the back end
	UploadedSelfieId = "uploadedSelfieId",
	// ID segmentation
	VidIdType = "vidIdType",
	VidInternationalIdType = "vidInternationalIdType",
	VidUsIdType = "vidUsIdType",
	VidOrganization = "vidOrganization",
	VidPersonalInformation = "vidPersonalInformation",
	VidScanResults = "vidScanResults",
	VidSSN = "vidSSN",
	VidSteps = "vidSteps",
	VidVoucher = "vidVoucher",
	VidWebOrder = "vidWebOrder",
	VoucherEmail = "voucherEmail",
	VoucherId = "voucherId",
}

// This is the type for each key in vIDStateName
export type VIDState = {
	[vIDStateName.BeamDataFileIds]: BeamDataFileIdsType;
	[vIDStateName.BeamDataScannedFileIds]: BeamDataFileIdsType;
	[vIDStateName.BeamInputFormData]?: BeamData;
	[vIDStateName.BiometricConsent]: boolean;
	[vIDStateName.BiometricConsentAcceptanceId]?: string;
	[vIDStateName.CaptureMethod]?: IdCaptureMethod;
	[vIDStateName.CameraAvailable]: boolean;
	[vIDStateName.CollectSSN]: boolean;
	[vIDStateName.CompletedVidSteps]: StringBooleanType;
	[vIDStateName.CurrentAddress]?: DisclosureAddress;
	[vIDStateName.DisclosureAcceptanceId]?: string;
	[vIDStateName.DisplayIdCaptureCamera]: boolean;
	[vIDStateName.DisplaySelfieCamera]: boolean;
	[vIDStateName.ElectedManualEntry]: boolean;
	[vIDStateName.ExtractedPhoto]: string | null;
	[vIDStateName.FailedUploadCount]: number;
	[vIDStateName.HasSeenExpiredIdWarning]: boolean;
	[vIDStateName.IsIdentityRecheck]: boolean;
	[vIDStateName.IsRemoteCaptureDevice]: boolean;
	[vIDStateName.KeyDetails]: KeyDetails;
	[vIDStateName.LatestScanJobId]: string;
	[vIDStateName.OrgContactInfo]?: OrgContactInfo;
	[vIDStateName.PassTypeBeamsData]?: BeamListQuery["beams"];
	[vIDStateName.ReceivedRemoteCaptureImages]: boolean;
	[vIDStateName.RemoteCaptureQRGenerated]: boolean;
	[vIDStateName.RescanIdClick]: boolean;
	[vIDStateName.ReturningOneAndDoneUser]: boolean;
	[vIDStateName.ResendCodeTime]: number;
	[vIDStateName.ShowDisclosures]: boolean;
	[vIDStateName.ShowManualEntryForAdditionalDetails]: boolean;
	[vIDStateName.ScanError]: ScanError | null;
	[vIDStateName.SkipLiveness]: boolean;
	[vIDStateName.EmbedVidWeb]: boolean;
	[vIDStateName.UploadedSelfieId]?: string;
	[vIDStateName.VidIdType]: IdCategory;
	[vIDStateName.VidInternationalIdType]: InternationalIdType;
	[vIDStateName.VidUsIdType]: UsIdType;
	[vIDStateName.VidOrganization]: Organization | null;
	[vIDStateName.VidPersonalInformation]: AdditionalPersonalInformation;
	[vIDStateName.VidScanResults]: ScanResult | null;
	[vIDStateName.VidSSN]: string;
	[vIDStateName.VidSteps]: StepType[];
	[vIDStateName.VidVoucher]: VidVoucherQuery["vIDVoucher"] | null;
	[vIDStateName.VidWebOrder]: Pick<Order, "id" | "fileNumber"> | null;
	[vIDStateName.VoucherEmail]: string;
	[vIDStateName.VoucherId]: string;
};

// These are the default values for each key in vIDStateName
export const defaultVIDStateValues: VIDState = {
	[vIDStateName.BeamDataFileIds]: {},
	[vIDStateName.BeamDataScannedFileIds]: {},
	[vIDStateName.BiometricConsent]: false,
	[vIDStateName.BiometricConsentAcceptanceId]: undefined,
	[vIDStateName.CaptureMethod]: undefined,
	[vIDStateName.CameraAvailable]: false,
	[vIDStateName.CollectSSN]: false,
	[vIDStateName.CompletedVidSteps]: defaultCompletedSteps,
	[vIDStateName.CurrentAddress]: undefined,
	[vIDStateName.DisclosureAcceptanceId]: undefined,
	[vIDStateName.DisplayIdCaptureCamera]: false,
	[vIDStateName.DisplaySelfieCamera]: false,
	[vIDStateName.ElectedManualEntry]: false,
	[vIDStateName.ExtractedPhoto]: null,
	[vIDStateName.FailedUploadCount]: 0,
	[vIDStateName.HasSeenExpiredIdWarning]: false,
	[vIDStateName.IsIdentityRecheck]: false,
	[vIDStateName.IsRemoteCaptureDevice]: false,
	[vIDStateName.KeyDetails]: { apiKey: "" },
	[vIDStateName.LatestScanJobId]: "",
	[vIDStateName.OrgContactInfo]: undefined,
	[vIDStateName.PassTypeBeamsData]: undefined,
	[vIDStateName.ReceivedRemoteCaptureImages]: false,
	[vIDStateName.RemoteCaptureQRGenerated]: false,
	[vIDStateName.RescanIdClick]: false,
	[vIDStateName.ReturningOneAndDoneUser]: false,
	[vIDStateName.ResendCodeTime]: 60,
	[vIDStateName.ShowDisclosures]: false,
	[vIDStateName.ShowManualEntryForAdditionalDetails]: false,
	[vIDStateName.ScanError]: null,
	[vIDStateName.SkipLiveness]: false,
	[vIDStateName.EmbedVidWeb]: false,
	[vIDStateName.UploadedSelfieId]: undefined,
	[vIDStateName.VidIdType]: IdCategory.US,
	[vIDStateName.VidInternationalIdType]: InternationalIdType.PASSPORT_BOOKLET,
	[vIDStateName.VidUsIdType]: UsIdType.STATE_ID,
	[vIDStateName.VidOrganization]: null,
	[vIDStateName.VidPersonalInformation]: { aliases: [], addresses: [] },
	[vIDStateName.VidScanResults]: null,
	[vIDStateName.VidSSN]: "",
	[vIDStateName.VidSteps]: defaultVidSteps,
	[vIDStateName.VidVoucher]: null,
	[vIDStateName.VidWebOrder]: null,
	[vIDStateName.VoucherEmail]: "",
	[vIDStateName.VoucherId]: "",
};

export const dataStoreAtom = atomWithStorage<DataStore>(
	"dataStore",
	{ [DEFAULT_STATE]: defaultVIDStateValues as VIDState },
	undefined,
	// Hydrate from localStorage when the module loads on the client so the in-memory store is never
	// briefly `{ defaultState }` only. Without this, any early `set(dataStoreAtom, prev => …)` (e.g. before
	// `onMount` sync) treats `prev` as the default and clobbers other voucher partitions in persisted state.
	{ getOnInit: typeof window !== "undefined" },
);

// The items in this object are specific to a voucherId
export interface KeyDetails {
	apiKey: string;
	apiKeyLocked?: boolean;
	isUserAlreadyRegistered?: boolean;
}

function blobToArrayBuffer(blob: any): Promise<ArrayBuffer> {
	return new Promise((resolve, reject) => {
		if (!(blob instanceof Blob)) {
			reject(new Error("The provided value is not a Blob."));
			return;
		}

		const reader = new FileReader();

		reader.onloadend = () => {
			const arrayBuffer = reader.result as ArrayBuffer;
			resolve(arrayBuffer);
		};

		reader.onerror = () => {
			reject(new Error("Failed to read blob as array buffer."));
		};

		reader.readAsArrayBuffer(blob);
	});
}

function getImageFromObjectStore(objectStore: IDBObjectStore, key: any, fileType: File["type"]): Promise<Blob> {
	return new Promise((resolve, reject) => {
		const request = objectStore.get(key);

		request.onerror = (event) => {
			console.error("Error getting image.", event);
			reject(new Error("Error getting image."));
		};

		request.onsuccess = () => {
			if (request.result) {
				const blob = new Blob([request.result], { type: fileType });
				resolve(blob);
			} else {
				reject(new Error("Image not found."));
			}
		};
	});
}

function blobToFile(blob: Blob, fileName: string) {
	const file = new File([blob], fileName, { type: blob.type });
	return file;
}

/** @summary Clears all vID IndexedDB image object stores; no-ops when `indexedDB` is unavailable (SSR / tests). */
export const clearIndexedDB = () => {
	if (typeof window === "undefined" || !window.indexedDB) {
		return;
	}

	const dbOpenRequest = window.indexedDB.open("vid", 1);
	dbOpenRequest.onerror = (event) => {
		// handle these errors!, e.g. When no DB is available, fallback to
		// localStorage?
		console.error("Error opening database.", event);
	};
	dbOpenRequest.onupgradeneeded = () => {
		const db = dbOpenRequest.result;
		db.createObjectStore("images");
	};
	dbOpenRequest.onsuccess = () => {
		const db = dbOpenRequest.result;
		const transaction = db.transaction("images", "readwrite");
		const objectStore = transaction.objectStore("images");
		objectStore.clear();
	};
};

/**
 * @summary Clears vID IndexedDB image entries for a specific session.
 * @param vidSessionId - vID session identifier used as the key prefix.
 * @returns Promise that resolves when cleanup completes.
 */
export const clearIndexedDBForSession = (vidSessionId: string): Promise<void> => {
	if (typeof window === "undefined" || !window.indexedDB) {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		const dbOpenRequest = window.indexedDB.open("vid", 1);
		dbOpenRequest.onerror = (event) => {
			console.error("Error opening database.", event);
			resolve();
		};
		dbOpenRequest.onupgradeneeded = () => {
			const db = dbOpenRequest.result;
			if (!db.objectStoreNames.contains("images")) {
				db.createObjectStore("images");
			}
		};
		dbOpenRequest.onsuccess = () => {
			const db = dbOpenRequest.result;
			const transaction = db.transaction("images", "readwrite");
			transaction.oncomplete = () => resolve();
			transaction.onerror = (event) => {
				console.error("Error clearing indexedDB session data.", event);
				resolve();
			};

			const objectStore = transaction.objectStore("images");
			const keysRequest = objectStore.getAllKeys();
			keysRequest.onerror = (event) => {
				console.error("Error reading image keys.", event);
				resolve();
			};
			keysRequest.onsuccess = () => {
				const keys = keysRequest.result;
				for (const key of keys) {
					if (typeof key === "string" && key.startsWith(`${vidSessionId}_`)) {
						objectStore.delete(key);
					}
				}
			};
		};
	});
};

export const atomWithAsyncFileStorage = <TValue>(key: string, initialValue: TValue, getVidSessionId: () => string) => {
	const baseAtom = atomWithReset(initialValue);
	let db: IDBDatabase | null = null;

	baseAtom.onMount = (setValue) => {
		(async () => {
			const vidSessionId = getVidSessionId();
			const fileDetailsKey = `${vidSessionId}_${key}Details`;
			const fileBlobKey = `${vidSessionId}_${key}Blob`;

			const dbOpenRequest = window.indexedDB.open("vid", 1);
			dbOpenRequest.onerror = (event) => {
				// handle these errors!, e.g. When no DB is available, fallback to localStorage?
				console.error("Error opening database.", event);
			};
			dbOpenRequest.onupgradeneeded = () => {
				db = dbOpenRequest.result;
				db.createObjectStore("images");
			};
			dbOpenRequest.onsuccess = () => {
				db = dbOpenRequest.result;
				const transaction = db.transaction("images", "readonly");
				const objectStore = transaction.objectStore("images");
				const request = objectStore.get(fileDetailsKey);
				request.onerror = (event) => {
					console.error("Error getting image.", event);
				};
				request.onsuccess = () => {
					if (request.result) {
						getImageFromObjectStore(objectStore, fileBlobKey, request.result.type).then((blob) => {
							const file = blobToFile(blob, request.result.name);
							setValue(file as any);
						});
					}
				};
			};
		})();
	};

	const derivedAtom = atom(
		(get) => get(baseAtom),
		(_get, set, update: TValue | typeof RESET) => {
			const vidSessionId = getVidSessionId();
			const fileDetailsKey = `${vidSessionId}_${key}Details`;
			const fileBlobKey = `${vidSessionId}_${key}Blob`;

			// `manager.setSessionFileAtom(…, null)` clears files without using RESET; IndexedDB must match.
			const clearStorage = update === RESET || update === null;
			set(baseAtom, clearStorage ? initialValue : update);
			void (async () => {
				if (clearStorage) {
					db?.transaction("images", "readwrite").objectStore("images").delete(fileDetailsKey);
					db?.transaction("images", "readwrite").objectStore("images").delete(fileBlobKey);
					return;
				}
				if (!(update instanceof File)) {
					return;
				}
				const nextValue = await blobToArrayBuffer(update);
				const { name, type } = update;
				db?.transaction("images", "readwrite").objectStore("images").put({ name, type }, fileDetailsKey);
				db?.transaction("images", "readwrite").objectStore("images").put(nextValue, fileBlobKey);
			})();
		},
	);

	return derivedAtom;
};

// Note: File atoms are created per session via `useSessionFileAtom` / `manager.setSessionFileAtom` (not these module stubs).

// Prevent loading of sensitive saved data after logout with this atom
export const hideSensitiveDataAtom = atom<boolean>(false);
// Allow a sample ID to be used for testing
export const allowSampleIdAtom = atom<boolean>(false);
